package org.gymly.ui.admin

import androidx.compose.foundation.background
import androidx.compose.foundation.border
import androidx.compose.foundation.clickable
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.ColumnScope
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.RowScope
import androidx.compose.foundation.layout.Spacer
import androidx.compose.foundation.layout.fillMaxHeight
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.size
import androidx.compose.foundation.layout.widthIn
import androidx.compose.foundation.rememberScrollState
import androidx.compose.foundation.shape.CircleShape
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.foundation.verticalScroll
import androidx.compose.material3.AlertDialog
import androidx.compose.material3.Button
import androidx.compose.material3.Checkbox
import androidx.compose.material3.CircularProgressIndicator
import androidx.compose.material3.DropdownMenu
import androidx.compose.material3.DropdownMenuItem
import androidx.compose.material3.OutlinedButton
import androidx.compose.material3.OutlinedTextField
import androidx.compose.material3.Switch
import androidx.compose.material3.Text
import androidx.compose.material3.TextButton
import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.rememberCoroutineScope
import androidx.compose.runtime.setValue
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.draw.clip
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.unit.dp
import androidx.compose.ui.unit.sp
import androidx.compose.ui.window.Dialog
import kotlinx.coroutines.launch
import org.gymly.data.AdminRepo
import org.gymly.model.AsistenteAgente
import org.gymly.model.AsistenteConfiguracion
import org.gymly.model.AsistenteConversacionAuditada
import org.gymly.model.AsistenteDatosAgente
import org.gymly.model.AsistenteDatosProveedor
import org.gymly.model.AsistenteMensaje
import org.gymly.model.AsistentePersonalizacion
import org.gymly.model.AsistenteProveedor
import org.gymly.theme.AdminColors
import org.gymly.util.PickedImage
import org.gymly.util.openImagePicker
import org.gymly.util.userMessage
import kotlin.io.encoding.Base64
import kotlin.io.encoding.ExperimentalEncodingApi

private const val PASOS_MINIMOS = 1
private const val PASOS_MAXIMOS = 10
private const val ANCHO_MAXIMO = 760
private const val ANCHO_DIALOGO = 560
/** Tamaño máximo del logo que se sube desde la PC: unos 300 KB. */
private const val BYTES_MAXIMOS_LOGO = 300_000

private val COLORES_MARCA = listOf("#1E5BD8", "#0F766E", "#7C3AED", "#B45309", "#BE123C", "#334155")

/**
 * Configuración → Agentes. Una columna centrada con cuatro bloques: encendido, apariencia,
 * cuentas de IA y agentes, y auditoría de conversaciones. Requiere permisos de Configuración de seguridad.
 */
@Composable
fun SettingsAgentsPanel(compact: Boolean) {
    val scope = rememberCoroutineScope()
    var configuracion by remember { mutableStateOf<AsistenteConfiguracion?>(null) }
    var cargando by remember { mutableStateOf(true) }
    var ocupado by remember { mutableStateOf(false) }
    var error by remember { mutableStateOf<String?>(null) }
    var creandoProveedor by remember { mutableStateOf(false) }
    var editandoProveedor by remember { mutableStateOf<AsistenteProveedor?>(null) }
    var editandoAgente by remember { mutableStateOf<AsistenteAgente?>(null) }
    var auditoria by remember { mutableStateOf<List<AsistenteConversacionAuditada>>(emptyList()) }
    var auditoriaAbierta by remember { mutableStateOf<AsistenteConversacionAuditada?>(null) }
    var mensajesAuditados by remember { mutableStateOf<List<AsistenteMensaje>>(emptyList()) }
    var porEliminar by remember { mutableStateOf<AsistenteProveedor?>(null) }

    val dialogoAbierto = creandoProveedor || editandoProveedor != null || editandoAgente != null ||
        auditoriaAbierta != null || porEliminar != null

    /** Ejecuta una operación con el panel bloqueado y recarga la configuración al terminar. */
    fun operar(mensajeError: String, accion: suspend () -> Unit) {
        scope.launch {
            ocupado = true
            error = null
            try {
                accion()
                configuracion = AdminRepo.api.getConfiguracionAsistente()
            } catch (e: Throwable) {
                error = e.userMessage(mensajeError)
            } finally {
                ocupado = false
            }
        }
    }

    fun abrirAuditada(conversacion: AsistenteConversacionAuditada) {
        scope.launch {
            try {
                mensajesAuditados = AdminRepo.api.abrirConversacionAuditadaAsistente(conversacion.id)
                auditoriaAbierta = conversacion
            } catch (e: Throwable) {
                error = e.userMessage("No se pudo abrir la conversación.")
            }
        }
    }

    fun guardarProveedor(id: String?, datos: AsistenteDatosProveedor) {
        operar("No se pudo guardar la cuenta de IA.") {
            if (id == null) AdminRepo.api.crearProveedorAsistente(datos)
            else AdminRepo.api.actualizarProveedorAsistente(id, datos)
            creandoProveedor = false
            editandoProveedor = null
        }
    }

    fun guardarAgente(agente: AsistenteAgente, datos: AsistenteDatosAgente) {
        operar("No se pudo guardar la ficha del agente.") {
            AdminRepo.api.actualizarAgenteAsistente(agente.id, datos)
            editandoAgente = null
        }
    }

    fun guardarApariencia(datos: AsistentePersonalizacion) {
        operar("No se pudo guardar la apariencia.") {
            EstadoAsistenteCompartido.personalizacion = AdminRepo.api.guardarPersonalizacionAsistente(datos)
        }
    }

    LaunchedEffect(Unit) {
        try {
            configuracion = AdminRepo.api.getConfiguracionAsistente()
            auditoria = AdminRepo.api.auditarConversacionesAsistente()
        } catch (e: Throwable) {
            error = e.userMessage("No se pudo cargar la configuración del asistente.")
        } finally {
            cargando = false
        }
    }

    // Columna centrada con ancho máximo: en pantallas anchas no queda una franja vacía a la derecha.
    Box(Modifier.fillMaxWidth().verticalScroll(rememberScrollState()), contentAlignment = Alignment.TopCenter) {
        Column(
            Modifier.widthIn(max = ANCHO_MAXIMO.dp).fillMaxWidth().padding(vertical = 8.dp),
            verticalArrangement = Arrangement.spacedBy(16.dp),
        ) {
            Column(verticalArrangement = Arrangement.spacedBy(4.dp)) {
                Text("Asistente de IA", fontWeight = FontWeight.Bold, fontSize = 18.sp, color = AdminColors.Text)
                Text(
                    "Enciende el asistente, dale identidad, configura las cuentas de IA y revisa las conversaciones.",
                    color = AdminColors.TextMuted,
                    fontSize = 13.sp,
                )
            }

            val config = configuracion
            when {
                cargando -> CircularProgressIndicator(Modifier.padding(top = 8.dp))
                config == null -> Text(error ?: "", color = AdminColors.Error, fontSize = 13.sp)
                else -> {
                    EncendidoAsistente(
                        habilitado = config.habilitado,
                        chatLibre = config.chatLibre,
                        ocupado = ocupado,
                        onCambiar = { nuevo ->
                            operar("No se pudo cambiar el interruptor del asistente.") {
                                AdminRepo.api.cambiarEncendidoAsistente(nuevo)
                                EstadoAsistenteCompartido.estado = AdminRepo.api.estadoAsistente()
                            }
                        },
                    )

                    if (config.habilitado) {
                        PersonalizacionCard(
                            actual = marcaActual(),
                            ocupado = ocupado,
                            onGuardar = { guardarApariencia(it) },
                        )

                        Seccion("Cuentas de IA", "Cada cuenta es un modelo de Gemini o de Anthropic, con sus topes de tokens y de gasto.") {
                            Row(Modifier.fillMaxWidth(), verticalAlignment = Alignment.CenterVertically) {
                                Spacer(Modifier.weight(1f))
                                Button(onClick = { creandoProveedor = true }, enabled = !ocupado) { Text("Nueva cuenta") }
                            }
                            config.proveedores.forEach { proveedor ->
                                ProveedorCard(
                                    proveedor = proveedor,
                                    ocupado = ocupado,
                                    onEditar = { editandoProveedor = proveedor },
                                    onEliminar = { porEliminar = proveedor },
                                )
                            }
                            if (config.proveedores.isEmpty()) {
                                Text("Todavía no hay cuentas. Crea una para activar el chat libre.", color = AdminColors.TextMuted, fontSize = 13.sp)
                            }
                        }

                        Seccion("Agentes", "Un agente por modo. Desactivar un agente apaga su modo; si es Consulta, también las preguntas rápidas.") {
                            config.agentes.forEach { agente ->
                                AgenteCard(
                                    agente = agente,
                                    proveedores = config.proveedores,
                                    ocupado = ocupado,
                                    onActivo = { guardarAgente(agente, agente.datos(activo = it)) },
                                    onEditar = { editandoAgente = agente },
                                )
                            }
                        }

                        Seccion("Auditoría de conversaciones", "Ver una conversación de otro usuario deja constancia de quién la abrió.") {
                            if (auditoria.isEmpty()) {
                                Text("No hay conversaciones registradas.", color = AdminColors.TextMuted, fontSize = 13.sp)
                            }
                            auditoria.forEach { conversacion ->
                                AuditoriaFila(conversacion = conversacion, onAbrir = { abrirAuditada(conversacion) })
                            }
                        }
                    }
                }
            }

            if (!dialogoAbierto) error?.let { Text(it, color = AdminColors.Error, fontSize = 13.sp) }
        }
    }

    if (creandoProveedor) {
        ProveedorDialog(
            inicial = null,
            catalogo = configuracion?.catalogo.orEmpty(),
            ocupado = ocupado,
            error = error,
            onGuardar = { guardarProveedor(null, it) },
            onCerrar = { creandoProveedor = false },
        )
    }
    editandoProveedor?.let { proveedor ->
        ProveedorDialog(
            inicial = proveedor,
            catalogo = configuracion?.catalogo.orEmpty(),
            ocupado = ocupado,
            error = error,
            onGuardar = { guardarProveedor(proveedor.id, it) },
            onCerrar = { editandoProveedor = null },
        )
    }
    editandoAgente?.let { agente ->
        val config = configuracion
        AgenteDialog(
            agente = agente,
            proveedores = config?.proveedores.orEmpty(),
            herramientasDisponibles = config?.herramientasDisponibles.orEmpty(),
            ocupado = ocupado,
            error = error,
            onGuardar = { guardarAgente(agente, it) },
            onCerrar = { editandoAgente = null },
        )
    }
    porEliminar?.let { cuenta ->
        AlertDialog(
            onDismissRequest = { porEliminar = null },
            title = { Text("¿Eliminar la cuenta ${cuenta.nombre}?") },
            text = {
                Text("Se quita de la lista. El historial de gasto de las consultas que la usaron se conserva. Si un agente la usa, primero hay que cambiarla.")
            },
            confirmButton = {
                TextButton(onClick = {
                    operar("No se pudo eliminar la cuenta.") {
                        AdminRepo.api.eliminarProveedorAsistente(cuenta.id)
                        porEliminar = null
                    }
                }) { Text("Eliminar", color = AdminColors.Error) }
            },
            dismissButton = { TextButton(onClick = { porEliminar = null }) { Text("Cancelar") } },
        )
    }
    auditoriaAbierta?.let { conversacion ->
        AuditoriaDialog(
            conversacion = conversacion,
            mensajes = mensajesAuditados,
            onCerrar = { auditoriaAbierta = null },
        )
    }
}

/**
 * Ventana de formulario: título fijo, cuerpo con scroll y botones siempre visibles abajo.
 * Así un formulario largo no se corta y las acciones nunca quedan fuera de la pantalla.
 */
@Composable
internal fun DialogoFormulario(
    titulo: String,
    onCerrar: () -> Unit,
    cuerpo: @Composable ColumnScope.() -> Unit,
    pie: @Composable RowScope.() -> Unit,
) {
    Dialog(onDismissRequest = onCerrar) {
        Column(
            Modifier
                .widthIn(max = ANCHO_DIALOGO.dp)
                .fillMaxWidth()
                .fillMaxHeight(0.92f)
                .clip(RoundedCornerShape(24.dp))
                .background(AdminColors.Surface),
        ) {
            Text(
                titulo,
                fontWeight = FontWeight.Bold,
                fontSize = 18.sp,
                color = AdminColors.Text,
                modifier = Modifier.padding(start = 24.dp, top = 22.dp, end = 24.dp, bottom = 8.dp),
            )
            Column(
                Modifier
                    .weight(1f)
                    .fillMaxWidth()
                    .verticalScroll(rememberScrollState())
                    .padding(horizontal = 24.dp),
                verticalArrangement = Arrangement.spacedBy(12.dp),
                content = cuerpo,
            )
            Row(
                Modifier.fillMaxWidth().padding(16.dp),
                horizontalArrangement = Arrangement.End,
                verticalAlignment = Alignment.CenterVertically,
                content = pie,
            )
        }
    }
}

/** Bloque con título, descripción y contenido. Todas las secciones usan el mismo formato. */
@Composable
private fun Seccion(titulo: String, descripcion: String, contenido: @Composable () -> Unit) {
    Column(
        Modifier
            .fillMaxWidth()
            .clip(RoundedCornerShape(16.dp))
            .background(AdminColors.SurfaceContainer)
            .padding(16.dp),
        verticalArrangement = Arrangement.spacedBy(12.dp),
    ) {
        Column(verticalArrangement = Arrangement.spacedBy(2.dp)) {
            Text(titulo, fontWeight = FontWeight.SemiBold, fontSize = 15.sp, color = AdminColors.Text)
            Text(descripcion, color = AdminColors.TextMuted, fontSize = 12.sp)
        }
        contenido()
    }
}

/** Interruptor general: encendido o apagado, con una línea que dice qué significa. */
@Composable
private fun EncendidoAsistente(habilitado: Boolean, chatLibre: Boolean, ocupado: Boolean, onCambiar: (Boolean) -> Unit) {
    Row(
        Modifier
            .fillMaxWidth()
            .clip(RoundedCornerShape(16.dp))
            .background(AdminColors.SurfaceContainer)
            .padding(16.dp),
        verticalAlignment = Alignment.CenterVertically,
    ) {
        Column(Modifier.weight(1f), verticalArrangement = Arrangement.spacedBy(4.dp)) {
            Text(
                if (habilitado) "Asistente encendido" else "Asistente apagado",
                fontWeight = FontWeight.SemiBold,
                color = AdminColors.Text,
                fontSize = 15.sp,
            )
            Text(
                when {
                    !habilitado -> "No aparece en la app y no responde, ni el chat ni las preguntas rápidas."
                    chatLibre -> "Visible para quien tenga el permiso. El chat libre está listo."
                    else -> "Visible para quien tenga el permiso. El chat libre no tiene una cuenta activa con clave."
                },
                color = AdminColors.TextMuted,
                fontSize = 12.sp,
            )
        }
        Switch(checked = habilitado, enabled = !ocupado, onCheckedChange = onCambiar)
    }
}

/** Nombre, color y logo. El logo se elige desde la PC o se pega como dirección https. */
@Composable
private fun PersonalizacionCard(actual: AsistentePersonalizacion, ocupado: Boolean, onGuardar: (AsistentePersonalizacion) -> Unit) {
    var nombre by remember(actual) { mutableStateOf(actual.nombre) }
    var color by remember(actual) { mutableStateOf(actual.color) }
    var logo by remember(actual) { mutableStateOf(actual.logoUrl.orEmpty()) }
    var nombreArchivo by remember(actual) { mutableStateOf<String?>(null) }
    var errorLogo by remember(actual) { mutableStateOf<String?>(null) }

    Seccion("Apariencia", "Nombre, color y logo con los que el asistente se presenta.") {
        Row(
            Modifier
                .fillMaxWidth()
                .clip(RoundedCornerShape(28.dp))
                .background(colorDeHex(color))
                .padding(start = 8.dp, end = 18.dp, top = 8.dp, bottom = 8.dp),
            verticalAlignment = Alignment.CenterVertically,
        ) {
            MarcaAsistente(logoUrl = logo.ifBlank { null }, nombre = nombre, tamano = 30.dp)
            Spacer(Modifier.size(10.dp))
            Text(nombre.ifBlank { "Asistente" }, color = Color.White, fontWeight = FontWeight.SemiBold)
        }

        OutlinedTextField(
            value = nombre,
            onValueChange = { nombre = it },
            label = { Text("Nombre") },
            singleLine = true,
            modifier = Modifier.fillMaxWidth(),
        )

        Text("Logo", color = AdminColors.TextMuted, fontSize = 12.sp)
        Row(verticalAlignment = Alignment.CenterVertically, horizontalArrangement = Arrangement.spacedBy(8.dp)) {
            OutlinedButton(
                onClick = {
                    openImagePicker { elegida ->
                        errorLogo = null
                        if (elegida != null) {
                            val valido = validarLogo(elegida)
                            if (valido == null) {
                                logo = urlDeDatos(elegida)
                                nombreArchivo = elegida.fileName
                            } else {
                                errorLogo = valido
                            }
                        }
                    }
                },
                enabled = !ocupado,
            ) { Text("Elegir imagen de mi PC") }
            if (logo.isNotBlank()) {
                TextButton(onClick = { logo = ""; nombreArchivo = null }, enabled = !ocupado) { Text("Quitar logo") }
            }
        }
        nombreArchivo?.let { Text("Imagen elegida: $it", color = AdminColors.TextMuted, fontSize = 12.sp) }
        errorLogo?.let { Text(it, color = AdminColors.Error, fontSize = 12.sp) }

        OutlinedTextField(
            value = if (logo.startsWith("data:")) "" else logo,
            onValueChange = { logo = it; nombreArchivo = null },
            label = { Text("…o pega una dirección https de la imagen (opcional)") },
            singleLine = true,
            modifier = Modifier.fillMaxWidth(),
        )

        Text("Color", color = AdminColors.TextMuted, fontSize = 12.sp)
        Row(horizontalArrangement = Arrangement.spacedBy(10.dp)) {
            COLORES_MARCA.forEach { hex ->
                val seleccionado = hex.equals(color, ignoreCase = true)
                Box(
                    Modifier
                        .size(32.dp)
                        .clip(CircleShape)
                        .background(colorDeHex(hex))
                        .border(if (seleccionado) 3.dp else 0.dp, AdminColors.Text, CircleShape)
                        .clickable { color = hex },
                )
            }
        }

        Row(Modifier.fillMaxWidth(), horizontalArrangement = Arrangement.End) {
            Button(
                onClick = {
                    onGuardar(AsistentePersonalizacion(nombre.trim(), color, logo.trim().ifBlank { null }))
                },
                enabled = !ocupado && nombre.isNotBlank(),
            ) { Text("Guardar apariencia") }
        }
    }
}

/** Solo imágenes de hasta 300 KB. Devuelve el motivo si no sirve, o null si está bien. */
private fun validarLogo(imagen: PickedImage): String? = when {
    imagen.mimeType !in listOf("image/png", "image/jpeg", "image/webp", "image/gif") ->
        "Elige una imagen png, jpeg, webp o gif"
    imagen.bytes.size > BYTES_MAXIMOS_LOGO -> "La imagen pesa más de 300 KB. Usa una más pequeña"
    else -> null
}

/** La imagen se guarda dentro de la configuración como data URL, sin archivos aparte. */
@OptIn(ExperimentalEncodingApi::class)
private fun urlDeDatos(imagen: PickedImage): String =
    "data:${imagen.mimeType};base64,${Base64.Default.encode(imagen.bytes)}"

@Composable
private fun ProveedorCard(proveedor: AsistenteProveedor, ocupado: Boolean, onEditar: () -> Unit, onEliminar: () -> Unit) {
    Column(
        Modifier
            .fillMaxWidth()
            .clip(RoundedCornerShape(12.dp))
            .background(AdminColors.Surface)
            .padding(14.dp),
        verticalArrangement = Arrangement.spacedBy(4.dp),
    ) {
        Row(verticalAlignment = Alignment.CenterVertically) {
            Text(proveedor.nombre, fontWeight = FontWeight.SemiBold, color = AdminColors.Text, fontSize = 15.sp, modifier = Modifier.weight(1f))
            Text(
                etiquetaTipo(proveedor),
                color = AdminColors.Primary,
                fontWeight = FontWeight.SemiBold,
                fontSize = 12.sp,
            )
        }
        Text("Modelo: ${proveedor.modelo}", color = AdminColors.TextMuted, fontSize = 12.sp)
        Text(
            buildString {
                append(if (proveedor.activo) "Activa" else "Inactiva")
                append(" · ")
                append(if (proveedor.tieneClave) "con clave" else "sin clave")
            },
            color = AdminColors.TextMuted,
            fontSize = 12.sp,
        )
        Text(
            "Gasto del mes: ${dolares(proveedor.gastoMesCents)}" +
                (if (proveedor.limiteMensualCents > 0) " de ${dolares(proveedor.limiteMensualCents)}" else " (sin límite)"),
            color = AdminColors.TextMuted,
            fontSize = 12.sp,
        )
        Text(
            "Tokens hoy: ${proveedor.tokensHoy}" +
                (if (proveedor.limiteDiarioTokens > 0) " de ${proveedor.limiteDiarioTokens}" else " (sin tope diario)") +
                " · máx. ${proveedor.maxTokensSalida} por respuesta",
            color = AdminColors.TextMuted,
            fontSize = 12.sp,
        )
        Spacer(Modifier.height(4.dp))
        Row(horizontalArrangement = Arrangement.spacedBy(8.dp)) {
            OutlinedButton(onClick = onEditar, enabled = !ocupado) { Text("Editar cuenta") }
            TextButton(onClick = onEliminar, enabled = !ocupado) { Text("Eliminar", color = AdminColors.Error) }
        }
    }
}

@Composable
private fun AgenteCard(
    agente: AsistenteAgente,
    proveedores: List<AsistenteProveedor>,
    ocupado: Boolean,
    onActivo: (Boolean) -> Unit,
    onEditar: () -> Unit,
) {
    Column(
        Modifier
            .fillMaxWidth()
            .clip(RoundedCornerShape(12.dp))
            .background(AdminColors.Surface)
            .padding(14.dp),
        verticalArrangement = Arrangement.spacedBy(6.dp),
    ) {
        Row(verticalAlignment = Alignment.CenterVertically) {
            Column(Modifier.weight(1f)) {
                Text(agente.nombre, fontWeight = FontWeight.SemiBold, color = AdminColors.Text, fontSize = 15.sp)
                Text(
                    if (agente.modo == "CONSULTA") "Una sola consulta" else "Hasta ${agente.maxPasos} consultas",
                    color = AdminColors.TextMuted,
                    fontSize = 12.sp,
                )
            }
            Switch(checked = agente.activo, enabled = !ocupado, onCheckedChange = onActivo)
        }
        Text(
            "Herramientas: ${agente.herramientas.joinToString(", ").ifEmpty { "ninguna" }}",
            color = AdminColors.TextMuted,
            fontSize = 12.sp,
        )
        Text(
            "Principal: ${nombreModelo(agente.proveedorPrincipalId, proveedores)} · Respaldo: ${nombreModelo(agente.proveedorRespaldoId, proveedores)}",
            color = AdminColors.TextMuted,
            fontSize = 12.sp,
        )
        OutlinedButton(onClick = onEditar, enabled = !ocupado) { Text("Editar ficha") }
    }
}

@Composable
private fun AuditoriaFila(conversacion: AsistenteConversacionAuditada, onAbrir: () -> Unit) {
    Column(
        Modifier
            .fillMaxWidth()
            .clip(RoundedCornerShape(12.dp))
            .background(AdminColors.Surface)
            .clickable { onAbrir() }
            .padding(12.dp),
        verticalArrangement = Arrangement.spacedBy(2.dp),
    ) {
        Text(conversacion.primeraPregunta, color = AdminColors.Text, fontSize = 14.sp, maxLines = 2)
        Text(
            "${conversacion.usuario} · ${conversacion.creadaEn.replace('T', ' ').take(16)}",
            color = AdminColors.TextMuted,
            fontSize = 12.sp,
        )
    }
}

@Composable
private fun AuditoriaDialog(conversacion: AsistenteConversacionAuditada, mensajes: List<AsistenteMensaje>, onCerrar: () -> Unit) {
    DialogoFormulario(
        titulo = "Conversación de ${conversacion.usuario}",
        onCerrar = onCerrar,
        cuerpo = {
            mensajes.forEach { mensaje ->
                Column {
                    Text(
                        if (mensaje.rol == "USUARIO") "Pregunta" else "Respuesta",
                        color = AdminColors.TextMuted,
                        fontSize = 11.sp,
                        fontWeight = FontWeight.SemiBold,
                    )
                    Text(mensaje.texto, color = AdminColors.Text, fontSize = 14.sp)
                }
            }
            Text("Esta apertura quedó registrada con tu usuario.", color = AdminColors.TextMuted, fontSize = 12.sp)
        },
        pie = {
            TextButton(onClick = onCerrar) { Text("Cerrar") }
        },
    )
}

/** Texto de ayuda bajo un campo: qué significa y qué poner. */
@Composable
internal fun AyudaCampo(texto: String) {
    Text(texto, color = AdminColors.TextMuted, fontSize = 11.sp)
}

@Composable
private fun AgenteDialog(
    agente: AsistenteAgente,
    proveedores: List<AsistenteProveedor>,
    herramientasDisponibles: List<String>,
    ocupado: Boolean,
    error: String?,
    onGuardar: (AsistenteDatosAgente) -> Unit,
    onCerrar: () -> Unit,
) {
    val esConsulta = agente.modo == "CONSULTA"
    var activo by remember { mutableStateOf(agente.activo) }
    var pasos by remember { mutableStateOf(agente.maxPasos) }
    var herramientas by remember { mutableStateOf(agente.herramientas.toSet()) }
    var principal by remember { mutableStateOf(agente.proveedorPrincipalId) }
    var respaldo by remember { mutableStateOf(agente.proveedorRespaldoId) }
    var textoInvalido by remember { mutableStateOf<String?>(null) }

    DialogoFormulario(
        titulo = "Ficha · ${agente.nombre}",
        onCerrar = onCerrar,
        cuerpo = {
            Row(verticalAlignment = Alignment.CenterVertically) {
                Text("Activo", color = AdminColors.Text, modifier = Modifier.weight(1f))
                Switch(checked = activo, onCheckedChange = { activo = it })
            }

            if (esConsulta) {
                Text("Una sola consulta por pregunta (fijo en este modo)", color = AdminColors.TextMuted, fontSize = 13.sp)
            } else {
                Row(verticalAlignment = Alignment.CenterVertically, horizontalArrangement = Arrangement.spacedBy(8.dp)) {
                    Text("Consultas máximas", color = AdminColors.Text, fontSize = 13.sp, modifier = Modifier.weight(1f))
                    OutlinedButton(
                        onClick = { pasos = (pasos - 1).coerceAtLeast(PASOS_MINIMOS) },
                        enabled = pasos > PASOS_MINIMOS,
                    ) { Text("−") }
                    Text("$pasos", color = AdminColors.Text, fontWeight = FontWeight.SemiBold)
                    OutlinedButton(
                        onClick = { pasos = (pasos + 1).coerceAtMost(PASOS_MAXIMOS) },
                        enabled = pasos < PASOS_MAXIMOS,
                    ) { Text("+") }
                }
            }

            Text("Herramientas", fontWeight = FontWeight.SemiBold, color = AdminColors.Text)
            herramientasDisponibles.forEach { herramienta ->
                Row(
                    Modifier
                        .fillMaxWidth()
                        .clickable {
                            herramientas = if (herramienta in herramientas) herramientas - herramienta else herramientas + herramienta
                        },
                    verticalAlignment = Alignment.CenterVertically,
                ) {
                    Checkbox(
                        checked = herramienta in herramientas,
                        onCheckedChange = {
                            herramientas = if (it) herramientas + herramienta else herramientas - herramienta
                        },
                    )
                    Text(herramienta, color = AdminColors.Text, fontSize = 13.sp)
                }
            }

            SelectorCuenta(
                titulo = "Cuenta principal",
                seleccionado = principal,
                proveedores = proveedores,
                permitirVacio = false,
                onSeleccion = { principal = it },
            )
            SelectorCuenta(
                titulo = "Cuenta de respaldo (si la principal falla)",
                seleccionado = respaldo,
                proveedores = proveedores,
                permitirVacio = true,
                onSeleccion = { respaldo = it },
            )

            (textoInvalido ?: error)?.let { Text(it, color = AdminColors.Error, fontSize = 12.sp) }
        },
        pie = {
            TextButton(onClick = onCerrar) { Text("Cancelar") }
            Spacer(Modifier.size(8.dp))
            Button(
                enabled = !ocupado,
                onClick = {
                    if (herramientas.isEmpty()) {
                        textoInvalido = "Elige al menos una herramienta"
                        return@Button
                    }
                    textoInvalido = null
                    onGuardar(AsistenteDatosAgente(
                        activo = activo,
                        maxPasos = pasos,
                        herramientas = herramientasDisponibles.filter { it in herramientas },
                        proveedorPrincipalId = principal,
                        proveedorRespaldoId = respaldo,
                    ))
                },
            ) { Text("Guardar") }
        },
    )
}

@Composable
private fun SelectorCuenta(
    titulo: String,
    seleccionado: String?,
    proveedores: List<AsistenteProveedor>,
    permitirVacio: Boolean,
    onSeleccion: (String?) -> Unit,
) {
    var abierto by remember { mutableStateOf(false) }
    Column(verticalArrangement = Arrangement.spacedBy(4.dp)) {
        Text(titulo, fontSize = 13.sp, color = AdminColors.TextMuted)
        Box {
            OutlinedButton(onClick = { abierto = true }) {
                Text(nombreModelo(seleccionado, proveedores))
            }
            DropdownMenu(expanded = abierto, onDismissRequest = { abierto = false }) {
                if (permitirVacio) {
                    DropdownMenuItem(
                        text = { Text("Ninguna") },
                        onClick = {
                            onSeleccion(null)
                            abierto = false
                        },
                    )
                }
                proveedores.forEach { proveedor ->
                    DropdownMenuItem(
                        text = { Text("${proveedor.nombre} (${proveedor.modelo})") },
                        onClick = {
                            onSeleccion(proveedor.id)
                            abierto = false
                        },
                    )
                }
            }
        }
    }
}

private fun nombreModelo(id: String?, proveedores: List<AsistenteProveedor>): String =
    proveedores.firstOrNull { it.id == id }?.let { "${it.nombre} (${it.modelo})" } ?: "Sin asignar"

private fun AsistenteAgente.datos(activo: Boolean = this.activo) = AsistenteDatosAgente(
    activo = activo,
    maxPasos = maxPasos,
    herramientas = herramientas,
    proveedorPrincipalId = proveedorPrincipalId,
    proveedorRespaldoId = proveedorRespaldoId,
)

/** Centavos de dólar a texto con dos decimales: 30 → "0.30". */
/** Nombre corto del tipo de cuenta para la lista: Gemini, Anthropic, o el host de un proveedor compatible. */
internal fun etiquetaTipo(proveedor: AsistenteProveedor): String = when (proveedor.tipo) {
    "ANTHROPIC" -> "Anthropic"
    "OPENAI" -> proveedor.baseUrl?.removePrefix("https://")?.substringBefore('/') ?: "Compatible con OpenAI"
    else -> "Gemini"
}

internal fun centavosATexto(centavos: Long): String =
    "${centavos / 100}.${(centavos % 100).toString().padStart(2, '0')}"

/** Texto con hasta dos decimales a centavos de dólar: "0.30" → 30. Null si no es un importe válido. */
internal fun importeACentavos(texto: String): Long? {
    val limpio = texto.trim().replace(',', '.')
    if (limpio.isEmpty()) return 0L
    val partes = limpio.split('.')
    if (partes.size > 2) return null
    val enteros = partes[0].ifEmpty { "0" }.toLongOrNull() ?: return null
    val decimales = (partes.getOrNull(1) ?: "").padEnd(2, '0')
    if (decimales.length > 2) return null
    val centavos = decimales.toLongOrNull() ?: return null
    if (enteros < 0) return null
    return enteros * 100 + centavos
}

private fun dolares(centavos: Long): String = "$" + centavosATexto(centavos)
